import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { buildPeriodRows, formatMonthDayRange } from '../../util/campingPeriods';
import { formatMoney } from '../../util/currency';

const { Money } = sdkTypes;

const rangeSx = { minWidth: { sm: '220px' } };

/**
 * One recurring period: its date range, the host's free text and — for seasons —
 * the nightly price that applies inside it.
 */
const PeriodRow = ({ icon, range, price, text }) => (
  <Stack alignItems={{ sm: 'center' }} direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
    <Stack alignItems="center" direction="row" spacing={1} sx={rangeSx}>
      {icon}
      <Typography variant="body2">{range}</Typography>
    </Stack>
    {price ? (
      <Typography fontWeight="bold" variant="body2">
        {price}
      </Typography>
    ) : null}
    {text ? (
      <Typography color="text.secondary" variant="body2">
        {text}
      </Typography>
    ) : null}
  </Stack>
);

const PeriodBlock = ({ children, hintId, titleId }) => (
  <Stack spacing={1}>
    <Typography component="h4" fontWeight="bold" variant="body1">
      <FormattedMessage id={titleId} />
    </Typography>
    <Typography color="text.secondary" variant="body2">
      <FormattedMessage id={hintId} />
    </Typography>
    <Stack spacing={0.5}>{children}</Stack>
  </Stack>
);

/**
 * Recurring closing periods and seasonal prices of a camping ground, shown inside
 * the "more information" section. Renders nothing when the listing has neither.
 *
 * The host's free texts (`label` on a season, `note` on a closing period) come
 * from the listing translation store when one exists for the viewer's locale,
 * keyed by the period's range — see `util/campingPeriods`.
 *
 * @param {object} props
 * @param {Array<object>} [props.closedPeriods] - `publicData.closedPeriods`
 * @param {Array<object>} [props.seasonalPrices] - `publicData.seasonalPrices`
 * @param {object} [props.translations] - Listing translations for the current locale
 * @returns {React.JSX.Element|null}
 */
const SectionCampingPeriodsMaybeComponent = ({ closedPeriods, seasonalPrices, translations }) => {
  const intl = useIntl();

  const closingRows = buildPeriodRows(closedPeriods, translations?.closedPeriods, 'note');
  const seasonRows = buildPeriodRows(seasonalPrices, translations?.seasonalPrices, 'label');

  if (closingRows.length === 0 && seasonRows.length === 0) {
    return null;
  }

  return (
    <Stack mt={2} spacing={3}>
      {closingRows.length > 0 ? (
        <PeriodBlock
          hintId="ListingPage.closingPeriodsHint"
          titleId="ListingPage.closingPeriodsTitle"
        >
          {closingRows.map((row) => (
            <PeriodRow
              icon={<EventBusyOutlinedIcon color="secondary" fontSize="small" />}
              key={row.key}
              range={formatMonthDayRange(intl, row)}
              text={row.text}
            />
          ))}
        </PeriodBlock>
      ) : null}

      {seasonRows.length > 0 ? (
        <PeriodBlock
          hintId="ListingPage.seasonalPricesHint"
          titleId="ListingPage.seasonalPricesTitle"
        >
          {seasonRows.map((row) => (
            <PeriodRow
              icon={<PaidOutlinedIcon color="secondary" fontSize="small" />}
              key={row.key}
              // A hand-edited season may carry no price; it then simply shows the
              // range and falls back to the base price at booking time.
              price={
                row.price?.currency
                  ? intl.formatMessage(
                      { id: 'ListingPage.seasonalPricePerNight' },
                      {
                        price: formatMoney(intl, new Money(row.price.amount, row.price.currency)),
                      }
                    )
                  : null
              }
              range={formatMonthDayRange(intl, row)}
              text={row.text}
            />
          ))}
        </PeriodBlock>
      ) : null}
    </Stack>
  );
};

SectionCampingPeriodsMaybeComponent.displayName = 'SectionCampingPeriodsMaybe';

const SectionCampingPeriodsMaybe = memo(SectionCampingPeriodsMaybeComponent);

export default SectionCampingPeriodsMaybe;
